/**
 * Batch iOS-P2 (2026-03-02) — Help & Feedback bot mount + suppression.
 *
 * Global mount that decides whether to render the HelpBot based on:
 *  * the current route (public / auth pages never show it)
 *  * the authenticated realm (candidate vs employer session)
 *  * a runtime suppression flag any page can toggle (used by
 *    AvTaskPage during active recording/uploading)
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import HelpBot from "@/components/HelpBot";
import { useAuth } from "@/contexts/AuthContext";
import { useCandidateAuth } from "@/contexts/CandidateAuthContext";

// ---------- Suppression context ----------
const HelpBotSuppressCtx = createContext({ suppressed: false, setSuppressed: () => {} });

export function HelpBotProvider({ children }) {
    const [suppressed, setSuppressed] = useState(false);
    const value = useMemo(() => ({ suppressed, setSuppressed }), [suppressed]);
    return (
        <HelpBotSuppressCtx.Provider value={value}>
            {children}
        </HelpBotSuppressCtx.Provider>
    );
}

// Hook for pages that need to hide the widget while a critical flow
// runs (e.g. AV recorder). ALWAYS pair a `useSuppressHelpBot(true)`
// on mount with a cleanup that restores false — the hook handles it.
export function useSuppressHelpBot(active) {
    const { setSuppressed } = useContext(HelpBotSuppressCtx);
    useEffect(() => {
        if (!active) return undefined;
        setSuppressed(true);
        return () => setSuppressed(false);
    }, [active, setSuppressed]);
}

// ---------- Route + realm resolver ----------

// Explicit deny-list. Login/register/OTP/public routes must NOT show
// the widget. Auth-gated candidate + employer routes DO.
const HIDDEN_PREFIXES = [
    "/login", "/register", "/verify-otp", "/forgot-password", "/reset-password",
    "/otp",   // legacy candidate OTP entry
    "/employer/login", "/employer/register",
    // Batch iOS-P2 spec: candidate onboarding + apply thank-you also hidden.
    "/candidate/login", "/candidate/register", "/candidate/verify-otp",
    "/candidate/forgot-password", "/candidate/reset-password",
    "/apply/thank-you",
];
// Public marketing pages — same treatment.
const PUBLIC_EXACT = new Set([
    "/", "/about", "/pricing", "/contact", "/privacy", "/terms",
    "/refund", "/careers",
]);

function isRouteEligible(pathname) {
    if (PUBLIC_EXACT.has(pathname)) return false;
    if (pathname.startsWith("/jobs/")) return false;   // public job listing / preview
    if (pathname === "/jobs") return false;
    for (const p of HIDDEN_PREFIXES) {
        if (pathname === p || pathname.startsWith(p + "/")) return false;
    }
    return true;
}

export default function HelpBotMount() {
    const location = useLocation();
    const { employer } = useAuth();
    const { candidate } = useCandidateAuth();
    const { suppressed } = useContext(HelpBotSuppressCtx);

    if (!isRouteEligible(location.pathname)) return null;

    // Decide the realm — employer wins on /dashboard + /bmi-ops routes;
    // everything else is candidate-side (apply, av-task, my dashboard,
    // screening pages, results).
    const pathname = location.pathname;
    const looksEmployer = pathname.startsWith("/dashboard") || pathname.startsWith("/bmi-ops");
    const realm = looksEmployer ? "employer" : "candidate";

    // Auth gate — anonymous users on gated routes are mid-redirect.
    const authed = looksEmployer ? !!employer : !!candidate;
    if (!authed) return null;

    return <HelpBot realm={realm} suppressed={suppressed} />;
}
